// =====================================================
// Swiss Finance OS - Avatar Upload Component
// Handles avatar image uploads with preview and validation
// =====================================================

import React, { useState, useRef } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { Upload, X, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getProfileInitials } from '@/lib/profile';

interface AvatarUploadProps {
  size?: 'sm' | 'md' | 'lg';
  showUploadButton?: boolean;
  onUploadComplete?: (avatarUrl: string) => void;
  className?: string;
}

export function AvatarUpload({
  size = 'md',
  showUploadButton = true,
  onUploadComplete,
  className = ''
}: AvatarUploadProps) {
  const { user, profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-16 w-16',
    lg: 'h-20 w-20'
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Ungültiger Dateityp',
        description: 'Bitte wählen Sie eine Bilddatei (JPG, PNG, GIF) aus.',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1024 * 1024) {
      toast({
        title: 'Datei zu groß',
        description: 'Die Datei darf maximal 1MB groß sein.',
        variant: 'destructive'
      });
      return;
    }

    await uploadAvatar(file);
  };

  const uploadAvatar = async (file: File) => {
    if (!user || !profile) return;

    setIsUploading(true);

    try {
      // Get file extension
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar.${fileExt}`;

      // Upload file to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true // Replace existing avatar
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL with cache-busting timestamp
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Add timestamp to force browser cache refresh
      const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      // Update user profile with new avatar URL
      const { error: updateError } = await supabase
        .from('users')
        .update({ avatar_url: avatarUrl })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      // Update local profile state
      await refreshProfile();

      toast({
        title: 'Avatar aktualisiert',
        description: 'Ihr Profilbild wurde erfolgreich hochgeladen.',
      });

      // Call completion callback
      onUploadComplete?.(avatarUrl);

    } catch (error) {
      console.error('Avatar upload error:', error);
      toast({
        title: 'Upload fehlgeschlagen',
        description: 'Beim Hochladen des Profilbilds ist ein Fehler aufgetreten.',
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const removeAvatar = async () => {
    if (!user || !(profile as any)?.avatar_url) return;

    setIsUploading(true);

    try {
      // Extract file name from avatar URL
      const url = new URL((profile as any).avatar_url);
      const fileName = url.pathname.split('/').slice(-2).join('/'); // Gets user_id/avatar.ext

      // Delete file from storage
      const { error: deleteError } = await supabase.storage
        .from('avatars')
        .remove([fileName]);

      if (deleteError) {
        console.warn('Storage delete error (file may not exist):', deleteError);
      }

      // Update user profile to remove avatar URL
      const { error: updateError } = await supabase
        .from('users')
        .update({ avatar_url: null })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      // Force cache update and refresh
      await refreshProfile();

      toast({
        title: 'Avatar entfernt',
        description: 'Ihr Profilbild wurde erfolgreich entfernt.',
      });

    } catch (error) {
      console.error('Avatar removal error:', error);
      toast({
        title: 'Fehler beim Entfernen',
        description: 'Beim Entfernen des Profilbilds ist ein Fehler aufgetreten.',
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={`flex flex-col items-center space-y-3 ${className}`}>
      <div className="relative">
        <Avatar className={`${sizeClasses[size]} ${isUploading ? 'opacity-50' : ''}`}>
          {(profile as any)?.avatar_url && (
            <AvatarImage 
              src={(profile as any).avatar_url}
              alt={`${profile.first_name} ${profile.last_name}`}
              key={`avatar-${(profile as any).avatar_url}`}
              className="object-cover"
            />
          )}
          <AvatarFallback className="bg-swiss-red text-white">
            {isUploading ? (
              <Loader2 className="h-1/2 w-1/2 animate-spin" />
            ) : (
              getProfileInitials(profile)
            )}
          </AvatarFallback>
        </Avatar>

        {/* Remove button for existing avatar */}
        {(profile as any)?.avatar_url && !isUploading && (
          <Button
            variant="destructive"
            size="sm"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
            onClick={removeAvatar}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {showUploadButton && (
        <div className="flex flex-col items-center space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center space-x-2"
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            <span>
              {(profile as any)?.avatar_url ? 'Bild ändern' : 'Bild hochladen'}
            </span>
          </Button>
          
          <p className="text-xs text-muted-foreground text-center">
            JPG, PNG oder GIF<br />
            Max. 1MB
          </p>
        </div>
      )}
    </div>
  );
}
